import React, { useEffect } from "react";
import questionPnc from "../../assets/questionPnc.svg";
import { useTranslation } from 'react-i18next';
import searchPnc from "../../assets/searchPnc.svg";
import "./UserMessage.css";

const UserMessage = ({ message, cycleId, id }) => {
  const { t, i18n } = useTranslation();
  if (!message) return null;

  // Function to convert month number to month name
  const getMonthName = (monthInput) => {
    // If the input is already a month name, return it
    const monthNames = [
      "January", "February", "March", "April", "May", "June", 
      "July", "August", "September", "October", "November", "December"
    ];
    
    // Check if the input is a number or a string that can be parsed as a number
    const monthNum = parseInt(monthInput);
    
    // If it's a valid month number between 1-12
    if (!isNaN(monthNum) && monthNum >= 1 && monthNum <= 12) {
      return monthNames[monthNum - 1];
    }
    
    // Otherwise return the original input
    return monthInput;
  };

  // Function to parse formatted user question text
  const parseFormattedText = (text) => {
    const extracted = {
      vin: "",
      mainGroup: "",
      subGroup: "",
      model: "",
      classification: "",
      publicationName: "",
      country: "",
      publicationNumber: "",
      calendar: "",
      queryText: "",
    };

    // Extract VIN
    const vinMatch = text.match(/\*\*(VIN(?:\/Chassis)?)\*\*:?\s*([^\n\\]+)/i);
    if (vinMatch && vinMatch[2]) {
      extracted.vin = vinMatch[2].trim();
    }

    // Extract Main Group
    const mainGroupMatch = text.match(/\*\*Main Group\*\*:?\s*([^\n\\]+)/i);
    if (mainGroupMatch && mainGroupMatch[1]) {
      extracted.mainGroup = mainGroupMatch[1].trim();
    }

    // Extract Sub Group
    const subGroupMatch = text.match(/\*\*Sub group\*\*:?\s*([^\n\\]+)/i);
    if (subGroupMatch && subGroupMatch[1]) {
      extracted.subGroup = subGroupMatch[1].trim();
    }

    // Extract Model
    const modelMatch = text.match(/\*\*Model\*\*:?\s*([^\n\\]+)/i);
    if (modelMatch && modelMatch[1]) {
      extracted.model = modelMatch[1].trim();
    }

    // Extract Classification
    const classMatch = text.match(/\*\*Classification\*\*:?\s*([^\n\\]+)/i);
    if (classMatch && classMatch[1]) {
      extracted.classification = classMatch[1].trim();
    }

    // Extract Publication Name
    const pubNameMatch = text.match(/\*\*Publication Name\*\*:?\s*([^\n\\]+)/i);
    if (pubNameMatch && pubNameMatch[1]) {
      extracted.publicationName = pubNameMatch[1].trim();
    }

    // Extract Country
    const countryMatch = text.match(/\*\*Country\*\*:?\s*([^\n\\]+)/i);
    if (countryMatch && countryMatch[1]) {
      extracted.country = countryMatch[1].trim();
    }

    // Extract Publication Number
    const pubNumberMatch = text.match(
      /\*\*Publication Number\*\*:?\s*([^\n\\]+)/i
    );
    if (pubNumberMatch && pubNumberMatch[1]) {
      extracted.publicationNumber = pubNumberMatch[1].trim();
    }

    // Extract Calendar/Calander if present (check various patterns)
    let calendarMatch = text.match(
      /\*\*(Calendar|Calander)\*\*:?\s*([^\n\\]+)/i
    );
    if (calendarMatch && calendarMatch[2]) {
      // Process the calendar string to convert month numbers to names
      const calendarValue = calendarMatch[2].trim();
      const parts = calendarValue.split(/\s+/);
      
      // If first part is a number, try to convert it to month name
      if (parts.length > 0) {
        parts[0] = getMonthName(parts[0]);
        extracted.calendar = parts.join(" ");
      } else {
        extracted.calendar = calendarValue;
      }
    } else {
      // Check for separate month and year pattern
      const monthMatch = text.match(/\*\*Month\*\*:?\s*([^\n\\]+)/i);
      const yearMatch = text.match(/\*\*Year\*\*:?\s*([^\n\\]+)/i);
      if (monthMatch && yearMatch) {
        const monthValue = getMonthName(monthMatch[1].trim());
        extracted.calendar = `${monthValue} ${yearMatch[1].trim()}`;
      }
    }

    // Extract the query part (look for text after all structured fields)
    const lines = text.split(/\\n|\n/);
    if (lines.length > 0) {
      // Find the last line that doesn't start with **
      for (let i = lines.length - 1; i >= 0; i--) {
        const line = lines[i].trim();
        if (line && !line.startsWith("**")) {
          extracted.queryText = line;
          break;
        }
      }
    }

    return extracted;
  };

  if (message.messageId && message.messageId.includes("history")) {
    const extracted = parseFormattedText(message.text);

    // Define color pairs for styling
    const colorPairs = [
      { labelBg: "bg-[#FFF1F1]", valueBg: "bg-[#FFFFFF]" },
      { labelBg: "bg-[#FFE2E2]", valueBg: "bg-[#EDEDED]" },
    ];

    // Create items to display
    const itemsToDisplay = [];

    // Add VIN if present
    if (extracted.vin) {
      itemsToDisplay.push({ label:t('vin'), value: extracted.vin });
    }

    // Add Publication Name if present
    if (extracted.publicationName) {
      itemsToDisplay.push({
        label: t('publicationNumber'),
        value: t(extracted.publicationName),
      });
    }

    // Add Model if present
    if (extracted.model) {
      itemsToDisplay.push({ label: t('modelName'), value: extracted.model });
    }

    // Add Country if present
    if (extracted.country) {
      itemsToDisplay.push({ label: t('country'), value: t(extracted.country) });
    }

    // Add Publication Number if present
    if (extracted.publicationNumber) {
      itemsToDisplay.push({
        label: t('publicationNo'),
        value: extracted.publicationNumber,
      });
    }

    // Add Calendar if present (translate month name)
    if (extracted.calendar) {
      // Parse calendar to translate month name (format: "Month Year")
      const calendarParts = extracted.calendar.split(' ');
      const translatedCalendar = calendarParts.length === 2
        ? `${t(calendarParts[0])} ${calendarParts[1]}`
        : extracted.calendar;
      itemsToDisplay.push({ label:  t('calendar'), value: translatedCalendar });
    }

    // Add Classification if present
    if (extracted.classification) {
      itemsToDisplay.push({
        label: t('classificationShop'),
        value: extracted.classification,
      });
    }

    // Always add Main Group and Sub Group
    // itemsToDisplay.push({ label: "Main Group", value: extracted.mainGroup || "N/A" });
    // itemsToDisplay.push({ label: "Sub Group", value: extracted.subGroup || "N/A" });

    return (
      <div
        className="bg-gray-100 rounded-lg ml-auto max-w-[500px] userPartPnc"
        id={message.messageId}
        data-message-id={message.messageId}
        data-message-text={extracted.queryText}
      >
        {/* Selections Section */}
        <div className="queryOptionsPnc">
          <div className="selectionsPnc">
            <div className="selectWithIcon">
              <img src={searchPnc} alt={t('findAnything')} /> {t('selectionSummary')}:
            </div>
          </div>
          <div className="inputPartPnc">
            <div className="inOptionPnc">
              {itemsToDisplay.map((item, index) => {
                const colorPair = colorPairs[index % 2];
                const isFirst = index === 0;
                const isLast = index === itemsToDisplay.length - 1;

                return (
                  <div className="vinOptionPnc" key={index}>
                    <div
                      className={`label vinLabelPnc ${
                        colorPair.labelBg
                      } px-2 py-1 ${isFirst ? "rounded-tl-lg" : ""}`}
                    >
                      {item.label}
                    </div>
                    <div
                      className={`value ${colorPair.valueBg} px-2 py-1 ${
                        isFirst ? "rounded-tr-lg" : ""
                      }`}
                    >
                      {item.value || "N/A"}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Query Section */}
        <div className="questionPnc">
          <div className="questionIcoPnc">
            {/* <img src={questionPnc} alt="Question Icon" /> Query */}
          </div>
          <div>
            <p className="questionPartPnc">
              {extracted.queryText || message.text}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Handle plain text messages with formatted content (like "**Model**: FEA01\n**Country** Singapore...")
  if (message.text && message.text.includes("**") && !message.searchInfo) {
    const extracted = parseFormattedText(message.text);

    // Define color pairs for styling
    const colorPairs = [
      { labelBg: "bg-[#FFF1F1]", valueBg: "bg-[#FFFFFF]" },
      { labelBg: "bg-[#FFE2E2]", valueBg: "bg-[#EDEDED]" },
    ];

    // Create items to display
    const itemsToDisplay = [];

    // Add Model if present
    if (extracted.model) {
      itemsToDisplay.push({ label: t('modelName'), value: extracted.model });
    }

    // Add Country if present
    if (extracted.country) {
      itemsToDisplay.push({ label: t('country'), value: extracted.country });
    }

    // Add Publication Name if present
    if (extracted.publicationName) {
      itemsToDisplay.push({
        label: t('publicationNumber'),
        value: extracted.publicationName,
      });
    }

    // Add Calendar if present
    if (extracted.calendar) {
      itemsToDisplay.push({ label: t('calendar'), value: extracted.calendar });
    }

    // Add VIN if present
    if (extracted.vin) {
      itemsToDisplay.push({ label: t('vin'), value: extracted.vin });
    }

    // Add Publication Number if present
    if (extracted.publicationNumber) {
      itemsToDisplay.push({
        label: t('publicationNo'),
        value: extracted.publicationNumber,
      });
    }

    // Add Classification if present
    if (extracted.classification) {
      itemsToDisplay.push({
        label: t('classificationShop'),
        value: extracted.classification,
      });
    }

    // Add Main Group and Sub Group if present
    if (extracted.mainGroup) {
      itemsToDisplay.push({ label: "Main Group", value: extracted.mainGroup });
    }
    if (extracted.subGroup) {
      itemsToDisplay.push({ label: "Sub Group", value: extracted.subGroup });
    }

    // Only render if we have structured data or return regular message format
    if (itemsToDisplay.length > 0) {
      return (
        <div
          className="bg-gray-100 rounded-lg ml-auto max-w-[500px] userPartPnc"
          id={message.messageId}
          data-message-id={message.messageId}
          data-message-text={extracted.queryText || message.text}
        >
          {/* Selections Section */}
          <div className="queryOptionsPnc">
            <div className="selectionsPnc">
              <div className="selectWithIcon">
                <img src={searchPnc} alt={t('findAnything')} /> {t('selectionSummary')}:
              </div>
            </div>
            <div className="inputPartPnc">
              <div className="inOptionPnc">
                {itemsToDisplay.map((item, index) => {
                  const colorPair = colorPairs[index % 2];
                  const isFirst = index === 0;
                  const isLast = index === itemsToDisplay.length - 1;

                  return (
                    <div className="vinOptionPnc" key={index}>
                      <div
                        className={`label vinLabelPnc ${
                          colorPair.labelBg
                        } px-2 py-1 ${isFirst ? "rounded-tl-lg" : ""} ${
                          isLast ? "rounded-bl-lg" : ""
                        }`}
                      >
                        {item.label}
                      </div>
                      <div
                        className={`value ${colorPair.valueBg} px-2 py-1 ${
                          isFirst ? "rounded-tr-lg" : ""
                        } ${isLast ? "rounded-br-lg" : ""}`}
                      >
                        {item.value || "N/A"}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Query Section */}
          <div className="questionPnc">
            <div className="questionIcoPnc">
              {/* <img src={questionPnc} alt="Question Icon" /> Query */}
            </div>
            <div>
              <p className="questionPartPnc">
                {extracted.queryText || message.text}
              </p>
            </div>
          </div>
        </div>
      );
    }
  }

  if (message.searchInfo) {
    const hasVin = message.searchInfo.vin && message.searchInfo.vin !== "N/A";
    const colorPairs = [
      { labelBg: "bg-[#FFF1F1]", valueBg: "bg-[#FFFFFF]" },
      { labelBg: "bg-[#FFE2E2]", valueBg: "bg-[#EDEDED]" },
    ];

    // Prepare items to display
    const itemsToDisplay = [];

    // Add VIN if present
    if (hasVin) {
      itemsToDisplay.push({ label: t('vin'), value: message.searchInfo.vin });
    }

    // Add Publication Name if present
    if (
      message.searchInfo.publicationName &&
      message.searchInfo.publicationName !== "N/A"
    ) {
      itemsToDisplay.push({
        label: t('publicationNumber'),
        value: t(message.searchInfo.publicationName),
      });
    }

    // Add Model if present or when VIN is not present
    if (
      message.searchInfo.model &&
      (!hasVin || message.searchInfo.model !== "N/A")
    ) {
      itemsToDisplay.push({ label: t('modelName'), value: message.searchInfo.model });
    }

    // Add Country if present
    if (message.searchInfo.country && message.searchInfo.country !== "N/A") {
      itemsToDisplay.push({
        label: t('country'),
        value: t(message.searchInfo.country),
      });
    }

    // Add Publication Number if present
    if (
      message.searchInfo.publicationNumber &&
      message.searchInfo.publicationNumber !== "N/A"
    ) {
      itemsToDisplay.push({
        label: t('publicationNo'),
        value: message.searchInfo.publicationNumber,
      });
    }

    // Add Calendar/Calander if present (check various field combinations)
    if (
      (message.searchInfo.calendar && message.searchInfo.calendar !== "N/A") ||
      (message.searchInfo.calander && message.searchInfo.calander !== "N/A")
    ) {
      // Process the calendar value to convert month number to name
      let calendarValue = message.searchInfo.calendar || message.searchInfo.calander;
      const parts = calendarValue.split(/\s+/);

      // If first part is a number, convert it to month name then translate
      if (parts.length > 0) {
        parts[0] = t(getMonthName(parts[0]));
        calendarValue = parts.join(" ");
      }

      itemsToDisplay.push({
        label: t('calendar'),
        value: calendarValue,
      });
    } else if (message.searchInfo.month && message.searchInfo.year) {
      // If calendar is not present but month and year are available separately
      const monthValue = t(getMonthName(message.searchInfo.month));
      itemsToDisplay.push({
        label: t('calendar'),
        value: `${monthValue} ${message.searchInfo.year}`,
      });
    }

    // Add Classification if present or when VIN is not present
    if (
      message.searchInfo.classification &&
      (!hasVin || message.searchInfo.classification !== "N/A")
    ) {
      itemsToDisplay.push({
        label: "Classification",
        value: message.searchInfo.classification,
      });
    }

    // Always add Main Group and Sub Group if present
    if (message.searchInfo.mainGroup) {
      itemsToDisplay.push({
        label: "Main Group",
        value: message.searchInfo.mainGroup,
      });
    }
    if (message.searchInfo.subGroup) {
      itemsToDisplay.push({
        label: "Sub Group",
        value: message.searchInfo.subGroup,
      });
    }

    return (
      <div
        className="bg-gray-100 ml-auto max-w-[500px] userPartPnc custom-radius"
        id={message.messageId}
        data-message-id={message.messageId}
        data-message-text={message.text}
      >
        {/* Selections Section */}
        <div className="queryOptionsPnc">
          <div className="selectionsPnc">
            <div className="selectWithIcon">
              <img src={searchPnc} alt={t('findAnything')} /> {t('selectionSummary')}:
            </div>
          </div>
          <div className="inputPartPnc">
            <div className="inOptionPnc">
              {itemsToDisplay.map((item, index) => {
                // Get color pair based on index (alternating)
                const colorPair = colorPairs[index % 2];
                const isFirst = index === 0;
                const isLast = index === itemsToDisplay.length - 1;

                return (
                  <div className="vinOptionPnc" key={index}>
                    <div
                      className={`label vinLabelPnc ${
                        colorPair.labelBg
                      } px-2 py-1 ${isFirst ? "rounded-tl-lg" : ""} ${
                        isLast ? "rounded-bl-lg" : ""
                      }`}
                    >
                      {item.label}
                    </div>
                    <div
                      className={`value ${colorPair.valueBg} px-2 py-1 ${
                        isFirst ? "rounded-tr-lg" : ""
                      } ${isLast ? "rounded-br-lg" : ""}`}
                    >
                      {item.value || "N/A"}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Query Section */}
        <div className="questionPnc">
          <div className="questionIcoPnc"></div>
          <div>
            <p className="questionPartPnc">{message.text}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="bg-gray-100 rounded-lg ml-auto max-w-[80%] userPartPnc"
      id={message.messageId}
      data-message-id={message.messageId}
      data-message-text={message.text}
    >
      <div className="questionPnc">
        <div className="questionIcoPnc"></div>
        <div>
          <p className="questionPartPnc">{message.text}</p>
        </div>
      </div>
    </div>
  );
};

export default UserMessage;